// src/components/ProductShowcase.js

import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import ProductCard from './ProductCard';
import ContactFormModal from './ContactFormModal';
import ImageViewerModal from './ImageViewerModal';
import './ProductShowcase.css';

// --- 1. NEW HELPER FUNCTION ---
// This function checks if a path is a full URL. If not, it assumes it's
// a local path from the 'public' folder and ensures it starts with a '/'.
const ensureAbsoluteUrl = (path) => {
  if (!path || typeof path !== 'string' || path.trim() === '') {
    return ''; // Return an empty string for invalid input
  }
  // If it's already a full URL (http:// or https://), return it as is.
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  // If it's a correct relative path, return it as is.
  if (path.startsWith('/')) {
    return path;
  }
  // Otherwise, it's likely a relative path missing the slash. Add it.
  return `/${path}`;
};


// Categories array is unchanged
const categories = [
    { key: 'indoor', name: 'Indoor Lights', description: 'For interiors that inspire.', imageUrl: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1932&q=80' },
    { key: 'outdoor', name: 'Outdoor Lights', description: 'Illuminate your exteriors.', imageUrl: 'https://plus.unsplash.com/premium_photo-1679582754394-f923d24d3554?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D' },
    { key: 'decorative', name: 'Decorative Lights', description: 'Art pieces that light up.', imageUrl: 'https://images.unsplash.com/photo-1758668810867-61f3f593a5ad?q=80&w=764&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D' },
    { key: 'automation', name: 'Automation', description: 'Smart control solutions.', imageUrl: 'https://aulpha.com/wp-content/uploads/2023/08/Automated-Lighting-Control.jpg' },
];

const ProductShowcase = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [viewMode, setViewMode] = useState('categories');
  const [activeCategory, setActiveCategory] = useState(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [viewerImage, setViewerImage] = useState(null);
  
  // --- 2. UPDATED DATA FETCHING ---
  useEffect(() => {
    const GOOGLE_SHEET_URL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vS-GsNLTdQgbWo_nqcW94hYOXk6glJatoPFQXzOooUV_UZt8MMv219_Ga1eTwYx6l-t_MY9njdGws8L/pub?output=csv";

    Papa.parse(GOOGLE_SHEET_URL, {
      download: true,
      header: true,
      complete: (results) => {
        const parsedData = results.data.filter(row => row.id && row.id.trim() !== "");
        
        // Transform the data, applying the new helper function
        const transformedData = parsedData.map(product => ({
          ...product,
          // Use the helper to sanitize both the image and PDF paths
          imageUrl: ensureAbsoluteUrl(product.imageUrl),
          pdfUrl: ensureAbsoluteUrl(product.pdfUrl),
          tags: product.tags ? product.tags.split(',').map(tag => tag.trim()) : []
        }));
        
        setAllProducts(transformedData);
        setIsLoading(false);
      },
      error: (err) => {
        console.error("Error fetching or parsing data:", err);
        setError("Failed to load product data. Please try again later.");
        setIsLoading(false);
      }
    });
  }, []);

  const filteredProducts = allProducts.filter(p => activeCategory && p.tags.includes(activeCategory.key));

  const handleCategorySelect = (category) => {
    setActiveCategory(category);
    setViewMode('products');
  };

  const handleGoBack = () => {
    setViewMode('categories');
    setActiveCategory(null);
  };
  
  const handleDownloadClick = (product) => {
    setSelectedProduct(product);
    setIsFormModalOpen(true);
  };
  
  const handleDownloadTrigger = (productToDownload) => {
    if (!productToDownload || !productToDownload.pdfUrl || productToDownload.pdfUrl === '#') return;
    const link = document.createElement('a');
    link.href = productToDownload.pdfUrl;
    const fileName = productToDownload.pdfUrl.split('/').pop();
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImageClick = (imageUrl) => setViewerImage(imageUrl);
  const handleCloseViewer = () => setViewerImage(null);
  const handleCloseFormModal = () => {
    setIsFormModalOpen(false);
    setSelectedProduct(null);
  };
  
  if (isLoading) {
    return <div className="loading-state">Loading Products...</div>;
  }
  
  if (error) {
    return <div className="error-state">{error}</div>;
  }

  return (
    <div className="product-showcase-container">
      <header className="showcase-header">
        <h1>Our Product Collection</h1>
        <p>Explore our curated lighting solutions, designed for performance and aesthetic appeal.</p>
      </header>
      
      <div className={`view-wrapper ${viewMode === 'products' ? 'show-products' : 'show-categories'}`}>
        
        <main className="category-grid">
          {categories.map((cat) => (
            <div 
              key={cat.key} 
              className="category-card" 
              onClick={() => handleCategorySelect(cat)}
              style={{ backgroundImage: `url(${cat.imageUrl})` }}
            >
              <div className="category-card-content">
                <h3>{cat.name}</h3>
                <p>{cat.description}</p>
                <span className="category-card-cta">View Products →</span>
              </div>
            </div>
          ))}
        </main>

        <div className="product-view">
           <div className="product-view-header">
             <button onClick={handleGoBack} className="back-button">← Back to Categories</button>
             <h2>{activeCategory?.name}</h2>
           </div>
          <main className="product-grid">
            {filteredProducts.length > 0 ? (
              filteredProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onDownloadClick={handleDownloadClick}
                  onImageClick={handleImageClick}
                />
              ))
            ) : (
              <p className="no-products-message">
                No products found in this category yet. Please check back later!
              </p>
            )}
          </main>
        </div>
      </div>
      
      <ContactFormModal
        isOpen={isFormModalOpen}
        onClose={handleCloseFormModal}
        product={selectedProduct}
        onSkip={handleDownloadTrigger}
        onFormSubmit={handleDownloadTrigger}
      />
      <ImageViewerModal src={viewerImage} alt="Enlarged product view" onClose={handleCloseViewer} />
    </div>
  );
};

export default ProductShowcase;